
import React, { useMemo, useState, useEffect } from "react";
import "./PaymentDetails.css";
import Widgets from "../../../widgets";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { getNurseRequestDetails } from "../../../modules/NursignCare/ManageRequest/manageRequestThunk";
import api from "../../../api/api";
import strings from "../../../utils/strings";
import Icons from "../../../Assets/Icons";
import spinnerIcon from "../../../Assets/Icons/spinner-solid 1.png";
import IconFemale from "../../../Assets/Icons/venus-mars-solid 1.png";
import call from "../../../Assets/Icons/call.png";
import shields from "../../../Assets/Icons/shield-halved-solid 1.png";

const formatDisplayDate = (iso) => {
  if (!iso) return "-";
  const d = new Date(iso);
  if (isNaN(d)) return iso;
  return `${d.getDate().toString().padStart(2, "0")}/${(d.getMonth() + 1).toString().padStart(2, "0")}/${d.getFullYear()}`;
};

const formatDOB = (iso) => {
  if (!iso) return "-";
  const d = new Date(iso);
  if (isNaN(d)) return iso;
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  return `${d.getDate().toString().padStart(2, "0")} ${months[d.getMonth()]}, ${d.getFullYear()}`;
};

const formatTransactionDate = (iso) => {
  if (!iso) return "-";
  const d = new Date(iso);
  if (isNaN(d)) return iso;
  return `${d.getDate().toString().padStart(2, "0")}/${(d.getMonth() + 1).toString().padStart(2, "0")}/${d.getFullYear()}, ${d.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: true })}`;
};

const sessionTotal = (sess) =>
  (sess.additionalConsumables || []).reduce(
    (sum, c) => sum + parseFloat(c.consumable?.price || 0) * (c.quantity || 0),
    0
  );

const PaymentDetails = () => {
  const { t } = useTranslation();
  const nav = useNavigate();
  const location = useLocation();
  const { request, session: selectedSession } = location.state || {};
  const dispatch = useDispatch();
  // Extract request details from Redux, handling different response shapes
  const rawRequestDetails = useSelector((state) => state.manageRequest?.requestDetails);
  const requestDetails = useMemo(
    () => rawRequestDetails?.data?.request || rawRequestDetails?.request || rawRequestDetails || {},
    [rawRequestDetails]
  );

  // Debug logs for troubleshooting
  console.log('REQUEST DETAILS', requestDetails);
  console.log('PATIENT CONDITION', requestDetails?.patientCondition);
  console.log('DISTANCE KM', requestDetails?.distanceInKm);
  // Local UI state
  const [expandedSessions, setExpandedSessions] = useState({});
  // New state for payment flow
  const [showConfirmPayModal, setShowConfirmPayModal] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [transactionId, setTransactionId] = useState("");
  const [isPaymentLoading, setIsPaymentLoading] = useState(false);
  const [paymentInfo, setPaymentInfo] = useState(null);
  const [apiError, setApiError] = useState(null);
  const [distanceKm, setDistanceKm] = useState("");
  const [showAbscondModal, setShowAbscondModal] = useState(false);
  const [isAbscondLoading, setIsAbscondLoading] = useState(false);
  const [localPaymentStatus, setLocalPaymentStatus] = useState(null);


  const loading = request && !requestDetails;
  // Fetch request details on mount with error handling
  useEffect(() => {
    if (request && request._id) {
      dispatch(getNurseRequestDetails({ id: request._id }))
        .unwrap()
        .catch((err) => {
          setApiError(err?.message || 'Failed to load request details');
        });
    }
  }, [dispatch, request]);

  // Sync distance when request details are loaded
  useEffect(() => {
    if (request?.distanceInKm !== undefined) {
      setDistanceKm(String(request.distanceInKm));
    } else if (requestDetails?.distanceInKm !== undefined) {
      setDistanceKm(String(requestDetails.distanceInKm));
    }
  }, [request, requestDetails]);

  const patient = requestDetails?.patient || {};
  const address = requestDetails?.address || {};
  const allSessions = useMemo(
    () => requestDetails?.sessions || (selectedSession ? [selectedSession] : []),
    [requestDetails, selectedSession]
  );

  const sessionRows = useMemo(() =>
    allSessions.map((sess, idx) => {
      const consumables = sess.additionalConsumables || [];
      const total = sessionTotal(sess);
      const items = consumables.map((c, i) => ({
        slNo: String(i + 1).padStart(2, "0"),
        itemNo: c.consumable?.code || "-",
        itemName: c.consumable?.itemName?.en || "-",
        quantity: c.quantity || 0,
        costPerItem: parseFloat(c.consumable?.price || 0),
        totalFee: parseFloat(c.consumable?.price || 0) * (c.quantity || 0),
      }));
      const canExpand = items.length > 0;
      return {
        _id: sess._id,
        date: formatDisplayDate(sess.date),
        rawDate: sess.date,
        status: sess.additionalConsumablePaymentStatus,
        transactionId: sess.additionalConsumableTransactionId,
        items,
        total,
        canExpand,
        expanded: Boolean(expandedSessions[idx]),
      };
    }),
    [allSessions, expandedSessions]
  );

  const grandTotal = sessionRows.reduce((s, r) => s + r.total, 0);
  const grandPaid = sessionRows.reduce((s, r) => s + (r.status === "COMPLETED" ? r.total : 0), 0);
  const grandRemaining = grandTotal - grandPaid;

  const overallStatus = selectedSession?.additionalConsumablePaymentStatus || allSessions[0]?.additionalConsumablePaymentStatus || "OUTSTANDING";
  const effectiveStatus = localPaymentStatus ?? overallStatus;

  const toggleSession = (idx) => {
    if (!sessionRows[idx].canExpand) return;
    setExpandedSessions((prev) => ({ ...prev, [idx]: !prev[idx] }));
  };

  const statusLabel = (s) => {
    if (s === "OUTSTANDING") return t("Outstanding");
    if (s === "COMPLETED") return t("Paid");
    if (s === "ABSCONDED") return t("Absconded");
    return s || "-";
  };

  // Build options for the Patient Condition dropdown (single value)
  const conditionOptions = requestDetails?.patientCondition
    ? [{ value: requestDetails.patientCondition, label: requestDetails.patientCondition }]
    : [];
 const renderAddressWithBreak = (address) => {
  if (!address) return "-";

  const parts = [
    address.street,
    address.city,
    address.district,
    address.ward,
    address.province,
  ].filter(Boolean);

  // Remove duplicate values
  const uniqueParts = [...new Set(parts)];

  return `${uniqueParts.join(", ")} - ${address.postalCode || ""}`;
};

  if (loading) {
    return (
      <div className="pd-loading" style={{ textAlign: "center", padding: "24px" }}>
        <img src={spinnerIcon} alt="loading" />
      </div>
    );
  }

  // Show API error if request details failed to load
  if (apiError) {
    return (
      <div className="pd-error" style={{ textAlign: "center", padding: "24px", color: "red" }}>
        <p>{t('errorLoadingRequest')}</p>
        <p>{apiError}</p>
      </div>
    );
  }
  return (
    <div className="payment-details-container">
      <Widgets.BreadCrumb
        breadcrumbs={["nursingCare", "additionalCostManagement", "paymentDetails"]}
        paths={[
          strings.navigation.nursingCareManageService,
          strings.navigation.additionalCostManagement,
          strings.navigation.paymentDetails,
        ]}
      />

      {/* Header */}
      <div className="pd-header">
        <h1 className="pd-title">{t("additionalBillingPaymentDetails")}</h1>
        <span className={`pd-status-badge ${effectiveStatus === "COMPLETED" ? "status-paid" : effectiveStatus === "ABSCONDED" ? "status-absconded" : "status-outstanding"}`}>
          {effectiveStatus === "OUTSTANDING" && <img src={spinnerIcon} alt="" className="pd-status-icon" />}
          {effectiveStatus === "COMPLETED" && <img src={Icons.checkSolid} alt="" className="pd-status-icon" />}
          {effectiveStatus === "ABSCONDED" && <img src={Icons.abIcon} alt="" className="pd-status-icon" />}
          {statusLabel(effectiveStatus)}
        </span>
      </div>

      {/* Patient Information Card */}
      <div className="pd-card">
        <div className="pd-card-header">
          <h2 className="pd-section-title">{t("patientInformation")}</h2>
          <button className="pd-view-request-btn" onClick={() => nav(-1)}>{t("viewRequestDetails")}</button>
        </div>

        <div className="pd-patient-content">
          <div className="pd-patient-left">
            <img
              src={patient.profilePhoto || "https://i.pravatar.cc/120?img=47"}
              alt="Patient"
              className="pd-patient-avatar"
            />
            <div className="pd-hn-box">
              <div className="pd-hn-label">{t("hospitalNumber")}</div>
              <div className="pd-hn-value">
                {patient.hnNumber || "-"}
              </div>
            </div>
          </div>

          <div className="pd-patient-right">
            <h3 className="pd-patient-name">{patient.fullName || "-"}</h3>

            <div className="pd-info-grid">
              <div className="pd-info-item">
                <img src={IconFemale} alt="" className="pd-icon" />
                <span>{patient.gender ? t(patient.gender) : "-"}</span>
              </div>
              <div className="pd-info-item dates">
                <img src={Icons.CalenderIcon} alt="" className="pd-icon" />
                <span>{formatDOB(patient.dateOfBirth)}</span>
              </div>
              <div className="pd-info-item">
                <img src={call} alt="" className="pd-icon" />
                <span>{patient.countryCode ? `+${patient.countryCode} ${patient.phoneNumber}` : patient.phoneNumber || "-"}</span>
              </div>
              <div className="pd-info-item gmails">
                <img src={Icons.SmsIcon} alt="" className="pd-icon" />
                <span>{patient.email || "-"}</span>
              </div>
            </div>

            <div className="pd-info-item pd-insurance insurence">
              <img src={shields} alt="" className="pd-icon" />
              <span>{patient.insuranceCategory ? t(patient.insuranceCategory) : "-"}</span>
            </div>

            {/* Patient Condition + Distance by KM — side by side */}
            <div className="ard-condition-zone-row" style={{ marginTop: '2rem' }}>
              <div className="ard-condition-col">
                <p className="ard-field-label">{t("patientCondition")}</p>
                <div className="ard-readonly-field">{requestDetails?.patientCondition || request?.patientCondition || "-"}</div>
              </div>
              <div className="ard-zone-col">
                <p className="ard-field-label">
                  {t("noofkm") || "DistanKM"}
                  <span className="asterik">*</span>
                </p>
                <div className="ard-zone-value">{distanceKm ? `${distanceKm} km` : "-"}</div>
              </div>
            </div>
          </div>
        </div>

        <div className="pd-address-zone-row">
          <div className="pd-address-section">
            <div className="pd-address-label">
              <img src={Icons.Location} alt="" className="pd-icon" />
              <span>{t("patientAddress")}</span>
            </div>
            <p className="pd-address-text">
              {renderAddressWithBreak(address)}
            </p>
          </div>
        </div>
      </div>

      {/* Sessions Breakdown Card */}
      <div className="pd-card">
        <div className="pd-card-header">
          <h2 className="pd-section-title">{t("sessionsBreakdown")}</h2>
        </div>

        <div className="pd-sessions-content">
          <table className="pd-sessions-table">
            <colgroup>
              <col className="session-date" />
              <col className="items-used" />
              <col className="total-fee" />
            </colgroup>
            <thead>
              <tr>
                <th>{t("sessionDate")}</th>
                <th>{t("itemsUsed")}</th>
                <th>{t("totalFee")}</th>
              </tr>
            </thead>
            <tbody>
              {sessionRows.length === 0 ? (
                <tr><td colSpan={3} style={{ textAlign: "center", padding: "16px", color: "#888" }}>{t("noData")}</td></tr>
              ) : sessionRows.map((session, index) => (
                <React.Fragment key={session._id || index}>
                  <tr className="pd-session-row">
                    <td>{session.date}</td>
                    <td>
                      {session.canExpand ? (
                        <button
                          type="button"
                          className="pd-collapse-link"
                          onClick={() => toggleSession(index)}
                          aria-expanded={session.expanded}
                        >
                          {session.expanded ? t("clickToCollapse") : t("clickToExpand")}
                        </button>
                      ) : (
                        <span className="pd-collapse-link pd-collapse-link-disabled" aria-disabled="true">
                          {t("availableAfterSessionDate")}
                        </span>
                      )}
                    </td>
                    <td className="pd-fee">${session.total.toFixed(2)}</td>
                  </tr>

                  {session.expanded && (
                    <tr className="pd-session-details-row">
                      <td colSpan="3">
                        <table className="pd-session-detail-table">
                          <colgroup>
                            <col className="slno" />
                            <col className="itemno" />
                            <col className="qty" />
                            <col className="cost" />
                            <col className="total" />
                          </colgroup>
                          <thead>
                            <tr>
                              <th>{t("slNo")}</th>
                              <th>{t("itemNo")}</th>
                              <th>{t("quantity")}</th>
                              <th>{t("costPerItem")}</th>
                              <th>{t("totalFee")}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {session.items.length > 0 ? (
                              <>
                                {session.items.map((item, itemIndex) => (
                                  <tr key={itemIndex} className="pd-detail-row">
                                    <td>{item.slNo}</td>
                                    <td>{item.itemNo}</td>
                                    <td>{item.quantity}</td>
                                    <td>${item.costPerItem.toFixed(2)}</td>
                                    <td>${item.totalFee.toFixed(2)}</td>
                                  </tr>
                                ))}
                                <tr className="pd-subtotal-row">
                                  <td colSpan="4">{t("total")}</td>
                                  <td>${session.total.toFixed(2)}</td>
                                </tr>
                              </>
                            ) : (
                              <tr className="pd-no-items-row">
                                <td colSpan="5">{t("noItemsUsed")}</td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Payment Details & Transaction History Card */}
      <div className="pd-card">
        <div className="pd-card-header">
          <h2 className="pd-section-title">{t("paymentDetailsAndTransactionHistory")}</h2>
        </div>

        <div className="pd-payment-content">
          {effectiveStatus === "OUTSTANDING" && (
            <table className="pd-payment-summary-table">
              <thead>
                <tr>
                  <th>{t("totalAmount")}</th>
                  <th>{t("amountPaid")}</th>
                  <th>{t("remainingBalance")}</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="pd-amount">${grandTotal.toFixed(2)}</td>
                  <td className="pd-amount">${grandPaid.toFixed(2)}</td>
                  <td className="pd-amount pd-balance">${grandRemaining.toFixed(2)}</td>
                </tr>
              </tbody>
            </table>
          )}


          {/* Payment Action Buttons — hidden once payment/absconding is done */}
          {effectiveStatus === "OUTSTANDING" && (
            <div className="pd-payment-actions">
              <button
                className="pd-pay-complete-btn"
                onClick={() => setShowConfirmPayModal(true)}
              >
                {t("payCompleteBill")}
              </button>
              <button
                className="pd-mark-absconding-btn"
                onClick={() => setShowAbscondModal(true)}
              >
                {t("markAsAbsconding")}
              </button>
            </div>
          )}



          {/* Transaction Table — shown only after payment or absconding is completed */}
          {effectiveStatus !== "OUTSTANDING" && (
            <table className="pd-transaction-table">
              <thead>
                <tr>
                  <th>{t("date")}</th>
                  <th>{t("amountPaid")}</th>
                  <th>{t("paymentStatus")}</th>
                  <th>{t("transactionId")}</th>
                </tr>
              </thead>
              {paymentSuccess && paymentInfo ? (
                <tbody>
                  <tr>
                    <td>{formatTransactionDate(paymentInfo.updatedAt || new Date().toISOString())}</td>
                    <td>{effectiveStatus === "COMPLETED" ? `$${grandTotal.toFixed(2)}` : "-"}</td>
                    <td>
                      <span className={`status-pill ${effectiveStatus === "COMPLETED" ? "status-paid" : "status-absconded"}`}>
                        {statusLabel(effectiveStatus)}
                      </span>
                    </td>
                    <td>{paymentInfo.additionalConsumableTransactionId || paymentInfo.transactionId || "-"}</td>
                  </tr>
                </tbody>
              ) : (
                <tbody>
                  {sessionRows.filter((s) => s.status !== "OUTSTANDING").length === 0 ? (
                    <tr>
                      <td colSpan="4" className="pd-no-data">{t("noData")}</td>
                    </tr>
                  ) : (
                    sessionRows
                      .filter((s) => s.status !== "OUTSTANDING")
                      .map((s, i) => (
                        <tr key={i}>
                          <td>{formatTransactionDate(s.rawDate)}</td>
                          <td>{s.status === "COMPLETED" ? `$${s.total.toFixed(2)}` : "-"}</td>
                          <td>
                            <span className={`status-pill ${s.status === "COMPLETED" ? "status-paid" : "status-absconded"}`}>
                              {statusLabel(s.status)}
                            </span>
                          </td>
                          <td>{s.transactionId || "-"}</td>
                        </tr>
                      ))
                  )}
                </tbody>
              )}
            </table>
          )}


        </div>
      </div>

      {showAbscondModal && (
        <div className="status-modal-overlay">
          <div className="status-modal open">
            <div className="status-modal-header">{t('markAsAbsconding')}</div>
            <div className="status-modal-sub">{t('abscondingConfirmDesc')}</div>
            <div className="status-modal-actions">
              <button className="status-cancel-btn" onClick={() => setShowAbscondModal(false)}>{t('cancel')}</button>
              <button
                className="status-confirm-btn"
                onClick={async () => {
                  const sessionId = selectedSession?._id || allSessions?.[0]?._id;
                  if (!sessionId) {
                    setApiError('Unable to determine session ID.');
                    return;
                  }
                  setIsAbscondLoading(true);
                  try {
                    const response = await api.patch(
                      `api/v1/cc/nursing_care/additional_consumables/${sessionId}/consumable_payment_status`,
                      { status: "ABSCONDED" }
                    );
                    setPaymentInfo(response.data);
                    setLocalPaymentStatus("ABSCONDED");
                    setPaymentSuccess(true);
                    setShowAbscondModal(false);
                  } catch (err) {
                    console.error('Absconding update failed', err);
                    setApiError(err?.response?.data?.message || 'Failed to update status.');
                  } finally {
                    setIsAbscondLoading(false);
                  }
                }}
                disabled={isAbscondLoading}
              >
                {isAbscondLoading ? 'Processing...' : t('confirm')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm payment modal */}
      {showConfirmPayModal && (
        <div className="status-modal-overlay">
          <div className="status-modal open">
            <div className="status-modal-header">{t('confirmPayment')}</div>
            <div className="status-modal-sub">
              {t('confirmPaymentDesc')}
            </div>
            <div className="status-modal-input">
              <input
                type="text"
                placeholder="Enter Transaction ID"
                value={transactionId}
                onChange={(e) => setTransactionId(e.target.value)}
                className="status-modal-input-field"
              />
            </div>
            <div className="status-modal-actions">
              <button className="status-cancel-btn" onClick={() => setShowConfirmPayModal(false)}>{t('cancel')}</button>
              <button
                className="status-confirm-btn"
                onClick={async () => {
                  if (!transactionId.trim()) {
                    setApiError('Transaction ID is required.');
                    return;
                  }
                  setIsPaymentLoading(true);
                  try {
                    const sessionId = selectedSession?._id || allSessions?.[0]?._id;
                    if (!sessionId) {
                      setApiError('Unable to determine session ID for payment.');
                      setIsPaymentLoading(false);
                      return;
                    }
                    const response = await api.patch(
                      `api/v1/cc/nursing_care/additional_consumables/${sessionId}/consumable_payment_status`,
                      { status: "COMPLETED", transactionId: transactionId.trim() }
                    );
                    setPaymentInfo(response.data);
                    setLocalPaymentStatus("COMPLETED");
                    setPaymentSuccess(true);
                    setShowConfirmPayModal(false);
                    setTransactionId("");
                  } catch (err) {
                    console.error('Payment update failed', err);
                    setApiError(err?.response?.data?.message || 'Payment failed. Please try again.');
                  } finally {
                    setIsPaymentLoading(false);
                  }
                }}
                disabled={isPaymentLoading}
              >
                {isPaymentLoading ? 'Processing...' : t('confirm')}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default PaymentDetails;